import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PinturaEditor } from '@pqina/react-pintura';

// pintura
import '@pqina/pintura/pintura.css';

import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,

  // plugins
  setPlugins,
  findNode,
  createMarkupEditorToolbar,
  removeNode,
  createNode,
  getEditorDefaults
} from '@pqina/pintura';
import CropSelector from './cropSelector';

import { useTranslate } from '@tolgee/react';
import {
  ALL,
  PLATFORM_LIST_NAMES,
  UPLOAD_TYPE_POST_MEDIA
} from '../../../../config/constants';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../../../../config/icons';
import { Button, ProgressBar } from 'react-bootstrap';
import {
  EditorImageConfig,
  generateFirebaseUploadUrl
} from '../../../../redux/slices/lomavisCreator/utils';
import { getLanguage } from '../../../../locales/language';
import { CROP_RATIOS } from '../../config/platformFeatures';
import {
  decimalToRatio,
  getImageDimensions,
  getSmartCrop, getValidMimeType,
  removeObjectsByKeys,
  scaleDownImage
} from '../../utils/helpers';
import { EDITOR_VIEW, UPLOAD_VIEW } from '../../config/constants';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import {
  addCropObject,
  clearEditedMediafile,
  deleteUnEditedMediafile,
  setMediaDataAttribute,
  updateMediaAssetFile,
  updateMultiImageEditorCloseState
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import axios from 'axios';
import axiosClient from '../../../../utils/axios';
import toast from 'react-hot-toast';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import { AddImageButton, EditorHeader } from '../sharedComponents';
import { platform } from 'os';
import ContentLoader from 'react-content-loader';
import { useTimeout } from 'react-use';

const getLocale = (lang) => {
  switch (lang) {
    case 'de':
    case 'de-DE':
      return {
        ...EditorImageConfig.DeLocale
      };
    case 'en':
    case 'en-GB':
    default:
      return {
        ...EditorImageConfig.EnLocale
      };
  }
};
const stringifyImageState = (imageState) => {
  return JSON.stringify(imageState, (k, v) => (v === undefined ? null : v));
};

const Editors = ({
  EditorArrayInfo,
  updateCurrentCrop,
  synchronousEditing,
  setSynchronousEditing,
  fileInputRef,
  setStartSubmission,
  startSubmission,
  successNotification,
  handleClose,
  handleCloseNoConfirmation,
  setCurrentIndex,
  currentIndex,
  totalItemsInUpload,
  scrollToTop
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const footerRef = useRef(null);
  const [locale, setLocale] = useState(getLocale(getLanguage()));

  const [editorsToLoad, setEditorsToLoad] = useState(2); // Start with 2 editors
  const [editorsReady, setEditorsReady] = useState(0); // Track how many editors have completed

  const [displaceWidth, setDisplaceWidth] = useState<number | null>(100);
  const [divWidth, setDivWidth] = useState<number | null>(null);
  const [topDivHeight, setTopDivHeight] = useState<number | null>(44);

  const [storedImageState, setStoredImageState] = useState(
    '{"cropLimitToImage": false }'
  );

  const [newEditArray, setNewEditArray] = useState([]);
  const [editorInitArray, setEditorInitArray] = useState([]);

  const [processingImage, setProcessingImage] = useState(false);

  const [progress, setProgress] = useState(0);

  const [currentView, setCurrentView] = useState(EDITOR_VIEW);
  const [currentImage, setCurrentImage] = useState('');
  const [editorUpdateCount, setEditorUpdateCount] = useState(0);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(
    EditorArrayInfo.map((item) => ({
      platform: item.platform,
      progress: 0
    }))
  );
  const [editorChanged, setEditorChanged] = useState(false);
  const [rightMargin, setRightMargin] = useState(0);

  const location_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const cloud_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.cloudUuid
  );

  const selectedFile = EditorArrayInfo[0]?.imageInfo[currentIndex];
  const preferredMimeType = getValidMimeType(selectedFile.file_type);

  const editorDefaults = getEditorDefaults({
    ...EditorImageConfig.ImageEditorDefaults,
    imageWriter: createDefaultImageWriter({
      mimeType: preferredMimeType,
      quality: 1.0,
      targetSize: {
        width: 1920,
        height: 1920,
        upscale: false
      }
    }),
    locale
  });

  const handleEditorProcess = (res) => {
    // 1. Turn imageState into string, replaces undefined values with null
    const imageStateStr = stringifyImageState(res.imageState);

    setStoredImageState(imageStateStr);
  };
  const handleEditor2Update = (imageState, index) => {
    if (imageState && imageState?.crop) {
      setNewEditArray((prevState) => {
        const existingIndex = prevState.findIndex(
          (item) => item.position === index
        );

        if (existingIndex !== -1) {
          // Replace the existing item
          return prevState.map((item, i) =>
            i === existingIndex
              ? { ...item, dimensions: imageState?.crop }
              : item
          );
        } else {
          // Add a new item
          return [
            ...prevState,
            { position: index, dimensions: imageState?.crop }
          ];
        }
      });
    }
  };

  const buildNewPinturaHistory = (cropData, index) => {
    const imageState =
      EditorArrayInfo[0].editorRef.current.editor.history.get()[
        EditorArrayInfo[0].editorRef.current.editor.history.get().length - 1
      ];

    EditorArrayInfo.filter((item) => item.index === index).map(
      async (edits) => {
        const newEditItem = newEditArray?.find(
          (item) => item?.position === index
        );
        const ImageDimensions = await getImageDimensions(
          EditorArrayInfo[index].imageInfo[currentIndex].assetBlob
            ? EditorArrayInfo[index + 1].imageInfo[currentIndex].assetBlob
            : EditorArrayInfo[index + 1].imageInfo[currentIndex]?.original_asset
        );

        const newEdit = cropData;

        const totalWidth = ImageDimensions?.width;
        const totalWidth2 = newEdit?.width;
        const totalHeight = ImageDimensions?.height;
        const totalHeight2 = newEdit?.height;
        //  imageState?.crop?.y * 2 + imageState?.crop?.height;

        const newXvalue = (value) => {
          const xFloat = parseFloat(value);
          const xRounded = Math.round(xFloat * 1e7) / 1e7;

          const percentWidth = (imageState?.crop?.width / totalWidth) * 100;
          const percentCropX = (imageState?.crop?.x / totalWidth) * 100;

          const absPercentOfMain =
            ((xRounded - percentCropX) / percentWidth) * 100;

          const relValueIn2 = (absPercentOfMain / 100) * totalWidth2;
          const absValueIn2 = (relValueIn2 + newEdit?.x) / totalWidth;

          return `${absValueIn2 * 100}%`;
        };
        const newYvalue = (value) => {
          const yFloat = parseFloat(value);
          const yRounded = Math.round(yFloat * 1e7) / 1e7;

          const percentHeight = (imageState?.crop?.height / totalHeight) * 100;
          const percentCropY = (imageState?.crop?.y / totalHeight) * 100;

          const absPercentOfMain =
            ((yRounded - percentCropY) / percentHeight) * 100;

          const relValueIn2 = (absPercentOfMain / 100) * totalHeight2;
          const absValueIn2 = (relValueIn2 + newEdit?.y) / totalHeight;

          return `${absValueIn2 * 100}%`;
        };

        function removeObjectsByKeys(object, keysToRemove) {
          return Object.keys(object).reduce((result, key) => {
            if (!keysToRemove.includes(key)) {
              result[key] = object[key];
            }
            return result;
          }, {});
        }

        const editPoints = (annotation) => {
          function isPercentage(value) {
            // Convert value to string
            const strValue = String(value);
            const percentageRegex = /^-?\d+(\.\d+)?%$/;
            return percentageRegex.test(strValue);
          }
          const newWidthValue = (value, isPercentage) => {
            const xFloat = parseFloat(value);
            const xRounded = Math.round(xFloat * 1e7) / 1e7;

            const newWidth =
              (newEdit?.width * xRounded) / imageState?.crop?.width;
            return value;
            if (isPercentage) {
              return `${newWidth}%`;
            } else {
              return newWidth;
            }
          };
          const newHeightValue = (value, isPercentage) => {
            const yFloat = parseFloat(value);
            const yRounded = Math.round(yFloat * 1e7) / 1e7;

            const newHeight =
              (newEdit?.height * yRounded) / imageState?.crop?.height;
            return value;
            if (isPercentage) {
              return `${newHeight}%`;
            } else {
              return newHeight;
            }
          };
          const calcX = (value) => {
            const xFloat = parseFloat(value);
            const relX =
              (xFloat - imageState?.crop?.x) / imageState?.crop?.width;
            const relX2 = relX * newEdit.width + newEdit?.x;
            return relX2;
          };

          const calcY = (value) => {
            const yFloat = parseFloat(value);
            const relY =
              (yFloat - imageState?.crop?.y) / imageState?.crop?.height;
            const relY2 = relY * newEdit.height + newEdit?.y;
            return relY2;
          };
          // Iterate over each annotation object
          const editedAnnotations = annotation.map((annot) => {
            // Modify the points array within the annotation object
            const editedPoints = annot?.points?.map((point) => ({
              x: isPercentage(point.x)
                ? newXvalue(point.x)
                : newXvalue((point.x / totalWidth) * 100),
              y: isPercentage(point.y)
                ? newYvalue(point.y)
                : newYvalue((point.y / totalHeight) * 100)
            }));

            const editedX1 = isPercentage(annot?.x1)
              ? newXvalue(annot?.x1)
              : newXvalue((annot?.x1 / totalWidth) * 100);
            const editedX2 = isPercentage(annot?.x2)
              ? newXvalue(annot?.x2)
              : newXvalue((annot?.x2 / totalWidth) * 100);
            const editedY1 = isPercentage(annot?.y1)
              ? newYvalue(annot?.y1)
              : newYvalue((annot?.y1 / totalHeight) * 100);
            const editedY2 = isPercentage(annot?.y2)
              ? newYvalue(annot?.y2)
              : newYvalue((annot?.y2 / totalHeight) * 100);
            const editedX = isPercentage(annot?.x)
              ? calcX((parseFloat(annot?.x) / 100) * totalWidth)
              : calcX(annot?.x);
            const editedY = isPercentage(annot?.y)
              ? calcY((parseFloat(annot?.y) / 100) * totalHeight)
              : calcY(annot?.y);
            // const editedRX = isPercentage(annot?.rx)
            //   ? newXvalue(annot?.rx)
            //   : newXvalue((annot?.rx / totalWidth) * 100);
            // const editedRY = isPercentage(annot?.ry)
            //   ? newYvalue(annot?.ry)
            //   : newYvalue((annot?.ry / totalHeight) * 100);
            const editedWidth = isPercentage(annot?.width)
              ? newWidthValue(annot?.width, true)
              : newWidthValue(annot?.width, false);
            const editedHeight = isPercentage(annot?.height)
              ? newHeightValue(annot?.height, true)
              : newHeightValue(annot?.height, false);
            const editedPathClose = annot?.pathClose;

            const filteredAnnots = removeObjectsByKeys(annot, [
              'pathClose',
              'points',
              'x1',
              'x2',
              'y1',
              'y2',
              'aspectRatio'
            ]);

            // Return the annotation object with the modified points

            return {
              ...filteredAnnots,
              ...(annot.x1 ? { x1: editedX1 } : {}),
              ...(annot.x2 ? { x2: editedX2 } : {}),
              ...(annot.y1 ? { y1: editedY1 } : {}),
              ...(annot.y2 ? { y2: editedY2 } : {}),

              ...(annot.x ? { x: editedX } : {}),
              ...(annot.y ? { y: editedY } : {}),
              // ...(annot.rx ? { rx: editedRX } : {}),
              // ...(annot.ry ? { ry: editedRY } : {}),
              ...(editedPoints ? { points: editedPoints } : {}),
              ...(annot.width
                ? {
                    width: editedWidth
                  }
                : {}),
              ...(annot.height
                ? {
                    height: editedHeight
                  }
                : {}),
              ...(annot.pathClose ? { pathClose: editedPathClose } : {}),
              ...(annot.aspectRatio ? { aspectRatio: annot?.aspectRatio } : {})
            };
          });

          return editedAnnotations;
        };

        const editRedactions = (redaction) => {
          function isPercentage(value) {
            // Convert value to string
            const strValue = String(value);
            const percentageRegex = /^-?\d+(\.\d+)?%$/;
            return percentageRegex.test(strValue);
          }
          const newWidthValue = (value) => {
            const xFloat = parseFloat(value);
            const xRounded = Math.round(xFloat * 1e7) / 1e7;

            const newWidth =
              (newEdit?.width * xRounded) / imageState?.crop?.width;

            return newWidth;
          };
          const newHeightValue = (value) => {
            const yFloat = parseFloat(value);
            const yRounded = Math.round(yFloat * 1e7) / 1e7;

            const newHeight =
              (newEdit?.height * yRounded) / imageState?.crop?.height;

            return newHeight;
          };
          const calcX = (value) => {
            const xFloat = parseFloat(value);
            const relX =
              (xFloat - imageState?.crop?.x) / imageState?.crop?.width;
            const relX2 = relX * newEdit?.width + newEdit?.x;
            return relX2;
          };

          const calcY = (value) => {
            const yFloat = parseFloat(value);
            const relY =
              (yFloat - imageState?.crop?.y) / imageState?.crop?.height;
            const relY2 = relY * newEdit?.height + newEdit?.y;
            return relY2;
          };
          const editedRedactions = redaction.map((redact) => {
            const editedX = calcX(redact?.x);

            const editedY = calcY(redact?.y);
            const editedWidth = newWidthValue(redact?.width);
            const editedHeight = newHeightValue(redact?.height);

            // Modify the points array within the annotation object

            const editedPathClose = redact?.pathClose;

            const filteredRedacts = removeObjectsByKeys(redact, ['pathClose']);

            // Return the annotation object with the modified points

            return {
              ...filteredRedacts,

              ...(redact.pathClose ? { pathClose: editedPathClose } : {}),
              ...(redact.x ? { x: editedX } : {}),
              ...(redact.y ? { y: editedY } : {}),
              ...(redact.width ? { width: editedWidth } : {}),
              ...(redact.height ? { height: editedHeight } : {})
            };
          });

          return editedRedactions;
        };

        const editedAnnotations = editPoints(imageState?.annotation);
        const editedRedactions = editRedactions(imageState?.redaction);

        // Update the original object with the modified annotations
        const updatedObject = {
          ...imageState,
          annotation: editedAnnotations,
          redaction: editedRedactions
        };

        let filteredState = removeObjectsByKeys(updatedObject, [
          'cropAspectRatio',
          'crop'
        ]);
        const imageStateStr = stringifyImageState(updatedObject);
        setStoredImageState(imageStateStr);
        //const lastRefArrayItem =
        //  edits.editorRef.current.editor.history.get()[
        //    edits.editorRef.current.editor.history.get().length - 1
        //  ];
        // const prevAnnotationHistory = lastRefArrayItem?.annotation;
        // const prevRedactionHistory = lastRefArrayItem?.redaction;
        // prevAnnotationHistory?.map((annot) => {
        //   if (
        //     !filteredState["annotation"]?.some((item) => item.id === annot.id)
        //   ) {
        //     filteredState["annotation"]?.push(annot);
        //   }
        // });
        // prevRedactionHistory?.map((redact) => {
        //   if (
        //     !filteredState["redaction"]?.some((item) => item.id === redact.id)
        //   ) {
        //     filteredState["redaction"]?.push(redact);
        //   }
        // });

        return edits.editorRef.current.editor.history.write(filteredState);

        // 2. Store imageStateStr on your server for late
      }
    );
  };

  const handleEditorUpdate = (imageState) => {
    if (!synchronousEditing) {
      return;
    }
    if (newEditArray.length === 0 || !newEditArray) {
      return;
    }

    EditorArrayInfo.slice(1, editorsToLoad).map(async (edits, index) => {
      const newEditItem = newEditArray?.find(
        (item) => item?.position === index
      );
      const ImageDimensions = await getImageDimensions(
        EditorArrayInfo[index + 1].imageInfo[currentIndex].assetBlob
          ? EditorArrayInfo[index + 1].imageInfo[currentIndex].assetBlob
          : EditorArrayInfo[index + 1].imageInfo[currentIndex]?.original_asset
      );

      const newEdit = newEditItem?.dimensions;

      const totalWidth = ImageDimensions?.width;
      const totalWidth2 = newEdit?.width;
      const totalHeight = ImageDimensions?.height;
      const totalHeight2 = newEdit?.height;
      //  imageState?.crop?.y * 2 + imageState?.crop?.height;

      const newXvalue = (value) => {
        const xFloat = parseFloat(value);
        const xRounded = Math.round(xFloat * 1e7) / 1e7;

        const percentWidth = (imageState?.crop?.width / totalWidth) * 100;
        const percentCropX = (imageState?.crop?.x / totalWidth) * 100;

        const absPercentOfMain =
          ((xRounded - percentCropX) / percentWidth) * 100;

        const relValueIn2 = (absPercentOfMain / 100) * totalWidth2;
        const absValueIn2 = (relValueIn2 + newEdit?.x) / totalWidth;

        return `${absValueIn2 * 100}%`;
      };
      const newYvalue = (value) => {
        const yFloat = parseFloat(value);
        const yRounded = Math.round(yFloat * 1e7) / 1e7;

        const percentHeight = (imageState?.crop?.height / totalHeight) * 100;
        const percentCropY = (imageState?.crop?.y / totalHeight) * 100;

        const absPercentOfMain =
          ((yRounded - percentCropY) / percentHeight) * 100;

        const relValueIn2 = (absPercentOfMain / 100) * totalHeight2;
        const absValueIn2 = (relValueIn2 + newEdit?.y) / totalHeight;

        return `${absValueIn2 * 100}%`;
      };

      function removeObjectsByKeys(object, keysToRemove) {
        return Object.keys(object).reduce((result, key) => {
          if (!keysToRemove.includes(key)) {
            result[key] = object[key];
          }
          return result;
        }, {});
      }

      const editPoints = (annotation) => {
        function isPercentage(value) {
          // Convert value to string
          const strValue = String(value);
          const percentageRegex = /^-?\d+(\.\d+)?%$/;
          return percentageRegex.test(strValue);
        }
        const newWidthValue = (value, isPercentage) => {
          const xFloat = parseFloat(value);
          const xRounded = Math.round(xFloat * 1e7) / 1e7;

          const newWidth =
            (newEdit?.width * xRounded) / imageState?.crop?.width;
          return value;
          if (isPercentage) {
            return `${newWidth}%`;
          } else {
            return newWidth;
          }
        };
        const newHeightValue = (value, isPercentage) => {
          const yFloat = parseFloat(value);
          const yRounded = Math.round(yFloat * 1e7) / 1e7;

          const newHeight =
            (newEdit?.height * yRounded) / imageState?.crop?.height;
          return value;
          if (isPercentage) {
            return `${newHeight}%`;
          } else {
            return newHeight;
          }
        };
        const calcX = (value) => {
          const xFloat = parseFloat(value);
          const relX = (xFloat - imageState?.crop?.x) / imageState?.crop?.width;
          const relX2 = relX * newEdit.width + newEdit?.x;
          return relX2;
        };

        const calcY = (value) => {
          const yFloat = parseFloat(value);
          const relY =
            (yFloat - imageState?.crop?.y) / imageState?.crop?.height;
          const relY2 = relY * newEdit.height + newEdit?.y;
          return relY2;
        };
        // Iterate over each annotation object
        const editedAnnotations = annotation.map((annot) => {
          // Modify the points array within the annotation object
          const editedPoints = annot?.points?.map((point) => ({
            x: isPercentage(point.x)
              ? newXvalue(point.x)
              : newXvalue((point.x / totalWidth) * 100),
            y: isPercentage(point.y)
              ? newYvalue(point.y)
              : newYvalue((point.y / totalHeight) * 100)
          }));

          const editedX1 = isPercentage(annot?.x1)
            ? newXvalue(annot?.x1)
            : newXvalue((annot?.x1 / totalWidth) * 100);
          const editedX2 = isPercentage(annot?.x2)
            ? newXvalue(annot?.x2)
            : newXvalue((annot?.x2 / totalWidth) * 100);
          const editedY1 = isPercentage(annot?.y1)
            ? newYvalue(annot?.y1)
            : newYvalue((annot?.y1 / totalHeight) * 100);
          const editedY2 = isPercentage(annot?.y2)
            ? newYvalue(annot?.y2)
            : newYvalue((annot?.y2 / totalHeight) * 100);
          const editedX = isPercentage(annot?.x)
            ? calcX((parseFloat(annot?.x) / 100) * totalWidth)
            : calcX(annot?.x);
          const editedY = isPercentage(annot?.y)
            ? calcY((parseFloat(annot?.y) / 100) * totalHeight)
            : calcY(annot?.y);
          // const editedRX = isPercentage(annot?.rx)
          //   ? newXvalue(annot?.rx)
          //   : newXvalue((annot?.rx / totalWidth) * 100);
          // const editedRY = isPercentage(annot?.ry)
          //   ? newYvalue(annot?.ry)
          //   : newYvalue((annot?.ry / totalHeight) * 100);
          const editedWidth = isPercentage(annot?.width)
            ? newWidthValue(annot?.width, true)
            : newWidthValue(annot?.width, false);
          const editedHeight = isPercentage(annot?.height)
            ? newHeightValue(annot?.height, true)
            : newHeightValue(annot?.height, false);
          const editedPathClose = annot?.pathClose;

          const filteredAnnots = removeObjectsByKeys(annot, [
            'pathClose',
            'points',
            'x1',
            'x2',
            'y1',
            'y2',
            'aspectRatio'
          ]);

          // Return the annotation object with the modified points

          return {
            ...filteredAnnots,
            ...(annot.x1 ? { x1: editedX1 } : {}),
            ...(annot.x2 ? { x2: editedX2 } : {}),
            ...(annot.y1 ? { y1: editedY1 } : {}),
            ...(annot.y2 ? { y2: editedY2 } : {}),

            ...(annot.x ? { x: editedX } : {}),
            ...(annot.y ? { y: editedY } : {}),
            // ...(annot.rx ? { rx: editedRX } : {}),
            // ...(annot.ry ? { ry: editedRY } : {}),
            ...(editedPoints ? { points: editedPoints } : {}),
            ...(annot.width
              ? {
                  width: editedWidth
                }
              : {}),
            ...(annot.height
              ? {
                  height: editedHeight
                }
              : {}),
            ...(annot.pathClose ? { pathClose: editedPathClose } : {}),
            ...(annot.aspectRatio ? { aspectRatio: annot?.aspectRatio } : {})
          };
        });

        return editedAnnotations;
      };

      const editRedactions = (redaction) => {
        function isPercentage(value) {
          // Convert value to string
          const strValue = String(value);
          const percentageRegex = /^-?\d+(\.\d+)?%$/;
          return percentageRegex.test(strValue);
        }
        const newWidthValue = (value) => {
          const xFloat = parseFloat(value);
          const xRounded = Math.round(xFloat * 1e7) / 1e7;

          const newWidth =
            (newEdit?.width * xRounded) / imageState?.crop?.width;

          return newWidth;
        };
        const newHeightValue = (value) => {
          const yFloat = parseFloat(value);
          const yRounded = Math.round(yFloat * 1e7) / 1e7;

          const newHeight =
            (newEdit?.height * yRounded) / imageState?.crop?.height;

          return newHeight;
        };
        const calcX = (value) => {
          const xFloat = parseFloat(value);
          const relX = (xFloat - imageState?.crop?.x) / imageState?.crop?.width;
          const relX2 = relX * newEdit?.width + newEdit?.x;
          return relX2;
        };

        const calcY = (value) => {
          const yFloat = parseFloat(value);
          const relY =
            (yFloat - imageState?.crop?.y) / imageState?.crop?.height;
          const relY2 = relY * newEdit?.height + newEdit?.y;
          return relY2;
        };
        const editedRedactions = redaction.map((redact) => {
          const editedX = calcX(redact?.x);

          const editedY = calcY(redact?.y);
          const editedWidth = newWidthValue(redact?.width);
          const editedHeight = newHeightValue(redact?.height);

          // Modify the points array within the annotation object

          const editedPathClose = redact?.pathClose;

          const filteredRedacts = removeObjectsByKeys(redact, ['pathClose']);

          // Return the annotation object with the modified points

          return {
            ...filteredRedacts,

            ...(redact.pathClose ? { pathClose: editedPathClose } : {}),
            ...(redact.x ? { x: editedX } : {}),
            ...(redact.y ? { y: editedY } : {}),
            ...(redact.width ? { width: editedWidth } : {}),
            ...(redact.height ? { height: editedHeight } : {})
          };
        });

        return editedRedactions;
      };

      const editedAnnotations = editPoints(imageState?.annotation);
      const editedRedactions = editRedactions(imageState?.redaction);

      // Update the original object with the modified annotations
      const updatedObject = {
        ...imageState,
        annotation: editedAnnotations,
        redaction: editedRedactions
      };

      // console.log("calculated value", updatedObject);

      let filteredState = removeObjectsByKeys(updatedObject, [
        'cropAspectRatio',
        'crop'
      ]);
      const imageStateStr = stringifyImageState(updatedObject);
      setStoredImageState(imageStateStr);
      // const lastRefArrayItem =
      //  edits.editorRef.current.editor.history.get()[
      //    edits.editorRef.current.editor.history.get().length - 1
      //  ];
      // const prevAnnotationHistory = lastRefArrayItem?.annotation;
      // const prevRedactionHistory = lastRefArrayItem?.redaction;
      // prevAnnotationHistory?.map((annot) => {
      //   if (
      //     !filteredState["annotation"]?.some((item) => item.id === annot.id)
      //   ) {
      //     filteredState["annotation"]?.push(annot);
      //   }
      // });
      // prevRedactionHistory?.map((redact) => {
      //   if (
      //     !filteredState["redaction"]?.some((item) => item.id === redact.id)
      //   ) {
      //     filteredState["redaction"]?.push(redact);
      //   }
      // });

      return edits.editorRef.current.editor.history.write(filteredState);

      // 2. Store imageStateStr on your server for late
    });
  };

  const willRenderToolbar = (toolbar) => {
    // get the export button
    const exportButton = findNode('export', toolbar);

    removeNode('export', toolbar);

    return toolbar;
  };

  useEffect(() => {
    setLocale(getLocale(getLanguage()));
  }, [getLanguage()]);
  const fetchCrops = async (Editor, index, item) => {
    let aspectRatio = CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height;

    let crop = async () => {
      if (!aspectRatio) {
        return undefined;
      } else {
        return await getSmartCrop(
          Editor?.imageInfo[currentIndex].assetBlob
            ? Editor.imageInfo[currentIndex].assetBlob
            : Editor.imageInfo[currentIndex].original_asset,
          CROP_RATIOS[item]?.width,
          CROP_RATIOS[item]?.height
        );
      }
    };
    const newCrop = await crop();
    // Update only the editor that corresponds to the given `Editor`
    if (Editor.editorRef?.current) {
      Editor.editorRef.current.editor.history.write({
        crop: newCrop,
        cropAspectRatio: CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height
      });
    }

    buildNewPinturaHistory(newCrop, index);
  };

  const fetchOneCrop = (platform) => {
    EditorArrayInfo.filter((item) => item.platform === platform).map(
      async (Editor) => {
        const currentImage = Editor?.imageInfo[currentIndex].assetBlob
          ? Editor.imageInfo[currentIndex].assetBlob
          : Editor.imageInfo[currentIndex].original_asset;

        const aspectRatio = async () => {
          const imageDimension = await getImageDimensions(currentImage);
          const currentImageAspectRatio = parseFloat(
            (imageDimension.width / imageDimension.height).toFixed(3)
          );
          const aspectRatios = Editor?.cropSuggestions?.map((item) =>
            parseFloat(
              (CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height).toFixed(3)
            )
          );

          if (aspectRatios?.includes(currentImageAspectRatio)) {
            const newCrop = Editor.cropSuggestions?.find(
              (item) =>
                parseFloat(
                  (
                    CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height
                  ).toFixed(3)
                ) === currentImageAspectRatio
            );

            updateCurrentCrop(Editor.platform, newCrop);
            return currentImageAspectRatio;
          } else {
            updateCurrentCrop(Editor.platform, Editor.defaultCropRatio);
            return (
              CROP_RATIOS[Editor.defaultCropRatio]?.width /
              CROP_RATIOS[Editor.defaultCropRatio]?.height
            );
          }
        };

        await aspectRatio();

        setEditorInitArray((prevEditors) => {
          // Check if the editor with the specific platform exists
          const editorExists = prevEditors.some(
            (editor) => editor.platform === Editor?.platform
          );

          if (editorExists) {
            // If it exists, update its isInitialized property
            return prevEditors.map((editor) =>
              editor.platform === Editor?.platform
                ? { ...editor, isInitialized: true }
                : editor
            );
          } else {
            // If it doesn't exist, add a new object for that platform
            return [
              ...prevEditors,
              { platform: Editor.platform, isInitialized: true } // New object added
            ];
          }
        });
      }
    );
  };

  const fetchAllCrops = () => {
    EditorArrayInfo.slice(0, editorsToLoad).map(async (Editor) => {
      const currentImage = Editor?.imageInfo[currentIndex].assetBlob
        ? Editor.imageInfo[currentIndex].assetBlob
        : Editor.imageInfo[currentIndex].original_asset;

      const aspectRatio = async () => {
        const imageDimension = await getImageDimensions(currentImage);
        const currentImageAspectRatio = parseFloat(
          (imageDimension.width / imageDimension.height).toFixed(3)
        );
        const aspectRatios = Editor?.cropSuggestions?.map((item) =>
          parseFloat(
            (CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height).toFixed(3)
          )
        );

        if (aspectRatios?.includes(currentImageAspectRatio)) {
          const newCrop = Editor.cropSuggestions?.find(
            (item) =>
              parseFloat(
                (CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height).toFixed(
                  3
                )
              ) === currentImageAspectRatio
          );

          updateCurrentCrop(Editor.platform, newCrop);
          return currentImageAspectRatio;
        } else {
          updateCurrentCrop(Editor.platform, Editor.defaultCropRatio);
          return (
            CROP_RATIOS[Editor.defaultCropRatio]?.width /
            CROP_RATIOS[Editor.defaultCropRatio]?.height
          );
        }
      };

      await aspectRatio();

      setEditorInitArray((prevEditors) => {
        // Check if the editor with the specific platform exists
        const editorExists = prevEditors.some(
          (editor) => editor.platform === Editor?.platform
        );

        if (editorExists) {
          // If it exists, update its isInitialized property
          return prevEditors.map((editor) =>
            editor.platform === Editor?.platform
              ? { ...editor, isInitialized: true }
              : editor
          );
        } else {
          // If it doesn't exist, add a new object for that platform
          return [
            ...prevEditors,
            { platform: Editor.platform, isInitialized: true } // New object added
          ];
        }
      });
    });
  };
  const editorDependencies = useMemo(() => {
    return EditorArrayInfo.map((item) => item?.defaultCropRatio);
  }, [EditorArrayInfo]);

  const previousDependencies = useRef(editorDependencies);

  useEffect(() => {
    const updateChangedEditorCrop = async () => {
      // Check which dependencies have changed
      if (editorsReady === EditorArrayInfo.length) {
        for (let i = 0; i < editorDependencies.length; i++) {
          if (previousDependencies.current[i] !== editorDependencies[i]) {
            const Editor = EditorArrayInfo[i];

            await fetchCrops(Editor, i, Editor?.defaultCropRatio); // Update only the changed editor
          }
        }
      } else {
        for (
          let i = 0;
          i < EditorArrayInfo.slice(0, editorInitArray.length).length;
          i++
        ) {
          const Editor = EditorArrayInfo[i];
          await fetchCrops(Editor, i, Editor?.defaultCropRatio); // Pass the current index 'i'
        }
      }

      // Update the previous dependencies reference
      previousDependencies.current = [...editorDependencies];
    };

    updateChangedEditorCrop();
  }, [editorDependencies, CROP_RATIOS]);

  // const fetchTheCrop = async (Editor, item) => {
  //   let aspectRatio = CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height;

  //   let crop = async () => {
  //     if (!aspectRatio) {
  //       return undefined;
  //     } else {
  //       return await getSmartCrop(
  //         Editor?.imageInfo[currentIndex].assetBlob
  //           ? Editor.imageInfo[currentIndex].assetBlob
  //           : Editor.imageInfo[currentIndex].original_asset,
  //         aspectRatio
  //       );
  //     }
  //   };

  //   // Update only the editor that corresponds to the given `Editor`
  //   if (Editor.editorRef?.current) {
  //     Editor.editorRef.current.editor.history.write({
  //       crop: await crop(),
  //       cropAspectRatio: CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height,
  //     });
  //   }
  // };

  const handleImageUpdate = async (editorRef, uploadedImage) => {
    if (!uploadedImage || !storedImageState) {
      return;
    }

    const currentState = JSON.parse(storedImageState);
    const justAnnotations = [...currentState?.annotation];
    const ImageDimensions = await getImageDimensions(uploadedImage);
    const filteredState = removeObjectsByKeys(currentState, [
      'annotation',
      'crop',
      'cropAspectRatio'
    ]);
    const editInfo = newEditArray[0].dimensions;

    const newAnnote = {
      aspectRatio: undefined,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: uploadedImage,
      backgroundSize: 'contain',
      cornerRadius: 0,
      disableErase: true,
      disableStyle: [
        'cornerRadius',
        'backgroundColor',
        'strokeColor',
        'strokeWidth'
      ],
      height: ImageDimensions?.height,
      id: uploadedImage,
      isEditing: false,
      isSelected: true,
      opacity: 1,
      rotation: 0,
      strokeColor: [0, 0, 0],
      strokeWidth: 0,
      width: ImageDimensions?.width,
      x: editInfo.x + editInfo.x / 2,
      y: editInfo.y
    };
    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, newAnnote]
    };

    const prevAnnotations = editorRef.current.editor.history
      .get()
      .slice(-1)[0]?.annotation;

    editorRef.current.editor.history.write({
      annotation: [...prevAnnotations, { ...newAnnote }]
    });

    editorRef.current.editor.util = undefined;
    editorRef.current.editor.util = 'sticker';
  };
  const handleArrayInputChange = (e) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);
      let assetBlob: any = URL.createObjectURL(file);

      EditorArrayInfo.map(async (item) => {
        if (file) {
          try {
            const resizedBlob = await scaleDownImage(file);
            const url = URL.createObjectURL(resizedBlob);

            handleImageUpdate(item.editorRef, url);
          } catch (error) {
            console.error(error);
            handleImageUpdate(item.editorRef, preview_image);
          }
        }
        item.editorRef.current.value = '';
      });
    });
    fileInputRef.current.value = '';
    // Edit the selected file
  };
  const handleInputChange = (e, editorRef) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);
      let assetBlob: any = URL.createObjectURL(file);

      if (file) {
        try {
          const resizedBlob = await scaleDownImage(file);
          const url = URL.createObjectURL(resizedBlob);

          handleImageUpdate(editorRef, url);
        } catch (error) {
          console.error(error);
          handleImageUpdate(editorRef, preview_image);
        }
      }
    });
    editorRef.current.value = '';
    // Edit the selected file
  };

  const exportImage = async () => {
    setProcessingImage(true);
    setStartSubmission(true);

    EditorArrayInfo.map((editor, index) => {
      const editorRef = editor.editorRef;

      if (editorRef.current) {
        // Get the edited image
        const editorHistory =
          editorRef.current.editor.history.get()[
            editorRef.current.editor.history.get().length - 1
          ];

        if (
          [
            'annotation',
            'decoration',
            'manipulation',
            'redaction',
            'selection'
          ].every((key) => editorHistory?.[key]?.length === 0) &&
          !editorHistory?.colorMatrix &&
          !editorHistory?.frame &&
          editorHistory?.rotation === 0
        ) {
          dispatch(
            addCropObject({
              platform: editor.platform,
              cropInfo: {
                width: editorHistory?.crop?.width, // width of the selected crop
                height: editorHistory?.crop?.height, // height of the selected crop
                x: editorHistory?.crop?.x, // start x coordinate of the crop starting
                y: editorHistory?.crop?.y, // start y coordinate of the crop starting
                uuid: editor.imageInfo[currentIndex]?.uuid, // uuid of the image the crop belongs to
                aspect_ratio_width: CROP_RATIOS[editor.defaultCropRatio]?.width, // select crop aspect ratio width
                aspect_ratio_height:
                  CROP_RATIOS[editor.defaultCropRatio]?.height // selected crop aspect ratio height
              }
            })
          );

          successNotification();

          setUploadCount(uploadCount + 1);
          dispatch(
            setMediaDataAttribute({
              platform: editor.platform,
              uuid: editor.imageInfo[currentIndex].uuid,
              attribute: 'newUpload',
              value: false
            })
          );

          dispatch(
            updateMultiImageEditorCloseState({
              state: false
            })
          );

          if (index === EditorArrayInfo.length - 1) {
            setEditorChanged(false);
            setUploadProgress((prevProgress) =>
              prevProgress.map((item) =>
                item.platform === editor.platform
                  ? { ...item, progress: 0 }
                  : item
              )
            );

            setStartSubmission(false);
            setProcessingImage(false);
            if (currentIndex + 1 >= totalItemsInUpload) {
              dispatch(
                clearEditedMediafile({
                  platform: ALL
                })
              );
              handleCloseNoConfirmation();
            } else {
              setCurrentIndex(currentIndex + 1);
            }
          }
          setNewEditArray([]);
          editorRef.current.editor.history.write([]);
          editorRef.value = '';
          scrollToTop();
          return;
        }

        editorRef.current.editor.processImage().then(async (result) => {
          // Handle the resulting image data (base64 or blob)
          const editedImage = result.dest; // This will be a blob

          const url = URL.createObjectURL(editedImage);

          const dimensions = await getImageDimensions(url);
          if (index === 0) {
            setCurrentImage(url);
            setProcessingImage(false);
          }

          setStartSubmission(true);
          index === 0 && setCurrentView(UPLOAD_VIEW);
          // Do something with the URL, e.g., display it, download it, etc.
          const selectedFile = editor.imageInfo[currentIndex];
          const uploadPreset = await generateFirebaseUploadUrl(
            {
              content_type: preferredMimeType,
              filesize_in_bytes: editedImage.size,
              filename: selectedFile.name,
              uuid: selectedFile.source_media_uuid ? selectedFile.uuid : null,
              location_uuid: location_uuid ? location_uuid : null,
              cloud_uuid: cloud_uuid ? cloud_uuid : null,
              width: dimensions?.width,
              height: dimensions?.height,
              upload_type: UPLOAD_TYPE_POST_MEDIA,
              source_media_uuid: selectedFile.source_media_uuid
                ? selectedFile.source_media_uuid
                : selectedFile.uuid
            },
            {},
            () => console.log('error here')
          );

          putWithFile(
            uploadPreset?.url,
            editedImage,
            uploadPreset?.headers,
            preferredMimeType,
            uploadPreset,
            editor.platform
          )
            .then((responseData) => {
              successNotification();
              setCurrentIndex(currentIndex + 1);

              dispatch(
                updateMediaAssetFile({
                  uuid: selectedFile?.uuid,
                  preview_image: url,
                  file: editedImage,
                  assetBlob: url,
                  width: dimensions?.width,
                  height: dimensions?.height,
                  platform: editor.platform,
                  uploadPresets: uploadPreset,
                  source_media_uuid: selectedFile.source_media_uuid
                    ? selectedFile.source_media_uuid
                    : selectedFile?.uuid
                })
              );
              setUploadCount(uploadCount + 1);
              scrollToTop();
              // setEditorUpdateCount(0);
              // dispatch(
              //   updateEditorUpdateState({
              //     platform: editor.platform,
              //     state: true,
              //     uuid: selectedFile?.uploadPreset.uuid,
              //   })
              // );
              dispatch(
                updateMultiImageEditorCloseState({
                  state: false
                })
              );

              if (index === EditorArrayInfo.length - 1) {
                setEditorChanged(false);
                setUploadProgress((prevProgress) =>
                  prevProgress.map((item) =>
                    item.platform === editor.platform
                      ? { ...item, progress: 0 }
                      : item
                  )
                );

                setStartSubmission(false);

                if (currentIndex + 1 >= totalItemsInUpload) {
                  handleCloseNoConfirmation();
                  dispatch(
                    clearEditedMediafile({
                      platform: ALL
                    })
                  );
                } else {
                  setCurrentIndex(currentIndex + 1);
                  setNewEditArray([]);
                  setCurrentView(EDITOR_VIEW);
                }

                // editorRef.current.editor.history.write({});
              }
            })
            .catch((error) => {
              if (index === EditorArrayInfo.length - 1) {
                setUploadProgress((prevProgress) =>
                  prevProgress.map((item) =>
                    item.platform === editor.platform
                      ? { ...item, progress: 0 }
                      : item
                  )
                );
                setStartSubmission(false);
                setCurrentView(EDITOR_VIEW);
              }
              console.error('Upload failed:', error);
            });
        });
      }
    });
  };

  const putWithFile = async (
    url,
    file,
    headers,
    file_type,
    uploadPreset,
    platform
  ) => {
    try {
      const config = {
        headers: headers ? { ...headers, 'Content-Type': file_type } : {} // Set headers property with provided headers
      };
      const response = await axios.put(url, file, {
        ...config,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress((prevProgress) =>
            prevProgress.map((item) =>
              item.platform === platform
                ? { ...item, progress: percentCompleted }
                : item
            )
          );
        }
      });

      try {
        const res = await axiosClient.put(uploadPreset?.confirm_url, {
          success: true
        });
        console.log('upload confirmation', res.data);

        return res.data;
      } catch (err: any) {
        console.log(err);
      }
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else {
        const res = error.response;

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Looks like there was a problem. Status Code: ${error}`);

        console.error('Error:', error);
        throw error;
      }
      toast.error(` ${t('errors.request_couldnt_be_completed')}`, {
        duration: 4000,
        position: 'top-right'
      });

      // Dispatch deleteAfile action inside the catch block
    }
  };
  const getTotalProgressPercentage = () => {
    const totalProgress = uploadProgress.reduce(
      (sum, item) => sum + item.progress,
      0
    );
    const numberOfPlatforms = uploadProgress.length;
    return numberOfPlatforms === 0 ? 0 : totalProgress / numberOfPlatforms;
  };

  useEffect(() => {
    dispatch(
      updateMultiImageEditorCloseState({
        state: true
      })
    );
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  useEffect(() => {
    const measureWidth = () => {
      // Select the child element with class 'nav_div' within the parent with class 'main'
      const element = document.querySelector('.main_editor .PinturaNavMain');
      const mainDiv = document.querySelector('.main_editor .PinturaTabPanels');
      const topDiv = document.querySelector('.main_editor .PinturaNavTools');

      const headerDiv = document.querySelector('.loading .headerDiv');
      const asideDiv = document.querySelector('.loading .asideDiv');
      const editorDiv = document.querySelector('.loading .editorDiv');

      if (element) {
        const divWidth = (element as HTMLElement).clientWidth;

        if (divWidth > 140) {
          setDisplaceWidth(0);
        } else {
          setDisplaceWidth((element as HTMLElement).clientWidth + 10); // Use clientWidth to get the width
        }
      }
      if (mainDiv) {
        const divWidth = (mainDiv as HTMLElement).clientWidth;

        setDivWidth(divWidth); // Use clientWidth to get the width
      }
      if (topDiv) {
        const divHeight = (topDiv as HTMLElement).clientHeight;

        setTopDivHeight(divHeight);
      }

      if (headerDiv) {
        const divWidth = (headerDiv as HTMLElement).clientWidth;
        const divHeight = (headerDiv as HTMLElement).clientHeight;

        setHeaderDimensions({ width: divWidth, height: divHeight });
      }
      if (asideDiv) {
        const divWidth = (asideDiv as HTMLElement).clientWidth;
        const divHeight = (asideDiv as HTMLElement).clientHeight;

        setAsideDimensions({ width: divWidth, height: divHeight });
      }
      if (editorDiv) {
        const divWidth = (editorDiv as HTMLElement).clientWidth;
        const divHeight = (editorDiv as HTMLElement).clientHeight;

        setEditorDivDimensions({ width: divWidth, height: divHeight });
      }
    };

    // Measure on mount
    measureWidth();

    setTimeout(() => {
      measureWidth();
    }, 1000);

    // Optionally, handle window resize events
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
    };
  }, []);

  const handleEditorInit = (platform) => {
    setEditorInitArray([
      ...editorInitArray,
      { platform: platform, isInitialized: false }
    ]);
  };
  const handleEditorReady = async (platform) => {
    fetchOneCrop(platform); // Update each editor one at a time

    setTimeout(() => {
      setEditorsReady((prev) => prev + 1);
    }, 500);

    setEditorInitArray((prevEditors) =>
      prevEditors.map((editor) =>
        editor.platform === platform
          ? { ...editor, isInitialized: true }
          : editor
      )
    );
  };
  const [componentKey, setComponentKey] = useState(0);
  useEffect(() => {
    setEditorInitArray([]);
    EditorArrayInfo?.map((editor) => {
      handleEditorInit(editor.platform);
    });
    setEditorsReady(0);
    setEditorsToLoad(2);
    setComponentKey((prevKey) => prevKey + 1);
  }, [currentIndex]);

  useEffect(() => {
    if (
      editorsReady > 0 &&
      editorsReady % 2 === 0 &&
      editorsToLoad < EditorArrayInfo.length
    ) {
      setEditorsToLoad((prev) => prev + 2); // Load next 2 editors
    }
  }, [editorsReady]);
  const updateChanges = () => {
    EditorArrayInfo.map((item, index) => {
      if (index !== 0) {
        return;
      }

      const editorRef = item.editorRef;

      if (editorRef.current) {
        // Get the edited image
        const editorHistory =
          editorRef.current.editor.history.get()[
            editorRef.current.editor.history.get().length - 1
          ];
        const oldUtil = editorRef.current.editor.util;

        handleEditorUpdate(editorHistory);
        editorRef.current.editor.util = undefined;
        editorRef.current.editor.util = oldUtil;
        editorRef.current.editor.history.redo();
      }
    });
  };
  useEffect(() => {
    const currentState = JSON.parse(storedImageState);

    setTimeout(() => {
      updateChanges();
    }, 3000);
  }, [editorDependencies]);

  const [headerDimensions, setHeaderDimensions] = useState({
    width: 0,
    height: 0
  });
  const [asideDimensions, setAsideDimensions] = useState({
    width: 0,
    height: 0
  });
  const [editorDivDimensions, setEditorDivDimensions] = useState({
    width: 0,
    height: 0
  });

  return (
    <div className="w-100 h-100">
      <div className="d-flex w-100 h-100 flex-column gap-6">
        {/* {processingImage && (
          <div
            style={{ zIndex: 10, top: "0px", left: "Opx" }}
            className="position-absolute w-100 h-100 bg-white bg-opacity-10% rounded-3"
          ></div>
        )} */}

        {currentView === EDITOR_VIEW && (
          <div
            style={{
              height: EditorArrayInfo?.length > 1 ? '' : '100%'
            }}
            className="d-flex w-100 flex-column gap-6"
          >
            {' '}
            <div
              style={{
                height: EditorArrayInfo?.length > 1 ? '' : '100%'
              }}
              className="d-flex gap-4 flex-column"
            >
              <EditorHeader>
                {' '}
                <div
                  style={{ flex: 1 }}
                  className="p-3 d-flex gap-4 align-items-center"
                >
                  <div className="d-flex gap-1 align-items-center">
                    {/* <i
                      className={
                        PLATFORM_ICONS[EditorArrayInfo[0].platform] +
                        " fs-1 me-2"
                      }
                      style={{
                        color: PLATFORM_COLORS[EditorArrayInfo[0].platform],
                      }}
                    ></i> */}
                    <i
                      className={PLATFORM_ICONS[EditorArrayInfo[0].platform]}
                      style={{
                        color: PLATFORM_COLORS[EditorArrayInfo[0].platform],
                        fontSize: '20px',
                        marginRight: '4px'
                      }}
                    ></i>{' '}
                    <div className="fs-3 text-dark">
                      {PLATFORM_LIST_NAMES?.[EditorArrayInfo[0].platform]}
                    </div>
                  </div>
                </div>
                {/* <div
                  style={{ flex: 1, display: 'none' }}
                  className=" align-items-center justify-content-center gap-2"
                >
                  <h3>
                    {' '}
                    {EditorArrayInfo[0].count + currentIndex + 1}/
                    {EditorArrayInfo[0].maxImages}
                  </h3>
                  <i
                    style={{ cursor: 'pointer', fontSize: '16px' }}
                    className="fa-duotone fa-circle-info edit_count-tooltip-trigger "
                    data-bs-toggle="tooltip"
                  ></i>
                </div> */}
                <div
                  style={{ flex: 1 }}
                  className="d-flex justify-content-end align-item-center"
                >
                  {synchronousEditing ? (
                    <Button
                      onClick={() => {
                        setSynchronousEditing(false);
                      }}
                      variant="secondary"
                      size="sm"
                      disabled={false}
                    >
                      <div className="">
                        {t('lomavisCreator.activate_singlePlatform')}
                      </div>
                    </Button>
                  ) : (
                    <AddImageButton
                      handleClick={() =>
                        EditorArrayInfo[0]?.fileInputRef?.current.click()
                      }
                      disabled={false}
                    />
                  )}
                  <input
                    ref={EditorArrayInfo[0].fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleInputChange(e, EditorArrayInfo[0]?.editorRef)
                    }
                    max={1}
                    style={{ display: 'none' }}
                  />
                </div>
              </EditorHeader>

              <div
                key={`${EditorArrayInfo[0].platform}-${componentKey}`}
                className="d-flex w-100 h-100 gap-4"
              >
                <div
                  style={{
                    overflowY: 'auto',
                    width: '100px',
                    height: EditorArrayInfo?.length > 1 ? '66.67vh' : '100%'
                  }}
                  className="bg-white p-2 rounded-3"
                >
                  {' '}
                  <CropSelector
                    items={EditorArrayInfo[0].cropSuggestions}
                    updateCurrentCrop={updateCurrentCrop}
                    selectedCrop={EditorArrayInfo[0].defaultCropRatio}
                    platform={EditorArrayInfo[0].platform}
                  />
                </div>

                <div
                  // style={{ minHeight: "500px" }}
                  className="w-100 h-100 flex-fill"
                >
                  {' '}
                  <div className=" w-100 h-100 position-relative d-flex flex-column gap-4 ">
                    {!editorChanged && (
                      <div
                        onClick={() => {
                          dispatch(
                            updateMultiImageEditorCloseState({
                              state: true
                            })
                          );
                          setEditorChanged(true);
                        }}
                        style={{ zIndex: 11, top: '0px', left: 'Opx' }}
                        className="position-absolute w-100 h-100 bg-opacity-20% rounded-3"
                      ></div>
                    )}{' '}
                    <div
                      style={{
                        height: EditorArrayInfo?.length > 1 ? '66.67vh' : '100%'
                      }}
                      className="w-100 bg-white p-3 rounded-3"
                    >
                      <PinturaEditor
                        {...editorDefaults}
                        className="main_editor"
                        ref={EditorArrayInfo[0].editorRef}
                        stickers={[
                          ['Emoji', [...EditorImageConfig?.Stickers]],
                          ['Markers', []]
                        ]}
                        src={
                          EditorArrayInfo[0].imageInfo[currentIndex]?.assetBlob
                            ? EditorArrayInfo[0].imageInfo[currentIndex]
                                ?.assetBlob
                            : EditorArrayInfo[0].imageInfo[currentIndex]
                                ?.original_asset
                        }
                        // imageCrop={{x: 0, y: 0, width: 64, height: 64}}
                        // imageCropAspectRatio={
                        //   CROP_RATIOS[EditorArrayInfo[0].defaultCropRatio]
                        //     ?.width /
                        //   CROP_RATIOS[EditorArrayInfo[0].defaultCropRatio]
                        //     ?.height
                        // }
                        cropInteractionFocus={'selection'}
                        willRenderToolbar={willRenderToolbar}
                        stickerStickToImage={true}
                        onReady={() => {
                          handleEditorReady(EditorArrayInfo[0].platform);
                        }}
                        onInit={() =>
                          handleEditorInit(EditorArrayInfo[0].platform)
                        }
                        enableZoom={false}
                        enableZoomControls={false}
                        onUpdate={handleEditorUpdate}
                        onProcess={handleEditorProcess}
                        imageCropLimitToImage={true}
                      ></PinturaEditor>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {EditorArrayInfo?.slice(1, editorsToLoad).map((Editor, index) => {
              return (
                <div
                  key={`${Editor?.platform}-editor`}
                  className="d-flex gap-4 flex-column"
                >
                  <div
                    style={{}}
                    className="h-100 w-100 h-60px d-flex justify-content-between bg-white p-3 rounded-3"
                  >
                    <div
                      style={{ flex: 1 }}
                      className=" p-3 d-flex gap-4 align-items-center"
                    >
                      <div className="d-flex gap-1 align-items-center">
                        <i
                          className={PLATFORM_ICONS[Editor.platform]}
                          style={{
                            color: PLATFORM_COLORS[Editor.platform],
                            fontSize: '20px',
                            marginRight: '4px'
                          }}
                        ></i>{' '}
                        <div className="fs-3 text-dark">
                          {PLATFORM_LIST_NAMES?.[Editor.platform]}
                        </div>
                      </div>
                    </div>
                    {/* <div
                      style={{ flex: 1, display: 'none' }}
                      className="align-items-center justify-content-center gap-2"
                    >
                      <h3>
                        {Editor.count + currentIndex + 1}/{Editor.maxImages}
                      </h3>
                      <i
                        style={{ cursor: 'pointer', fontSize: '16px' }}
                        className="fa-duotone fa-circle-info edit_count-tooltip-trigger "
                        data-bs-toggle="tooltip"
                      ></i>
                    </div> */}

                    <div
                      style={{ flex: 1 }}
                      className="d-flex justify-content-end align-item-center"
                    >
                      {synchronousEditing ? (
                        <Button
                          onClick={() => {
                            setSynchronousEditing(false);
                          }}
                          variant="secondary"
                          size="sm"
                          disabled={false}
                        >
                          <div className="">
                            {t('lomavisCreator.activate_singlePlatform')}
                          </div>
                        </Button>
                      ) : (
                        <AddImageButton
                          handleClick={() =>
                            Editor?.fileInputRef?.current.click()
                          }
                          disabled={false}
                        />
                      )}
                    </div>
                    <input
                      ref={Editor?.fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleInputChange(e, Editor?.editorRef)}
                      max={1}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div className="d-flex w-100 h-100 gap-4">
                    <div
                      style={{
                        overflowY: 'auto',
                        width: '100px',
                        height: '66.67vh'
                      }}
                      className="bg-white p-2 rounded-3"
                    >
                      {' '}
                      <CropSelector
                        items={Editor.cropSuggestions}
                        updateCurrentCrop={async (platform, item) => {
                          await fetchCrops(Editor, index, item);
                          updateCurrentCrop(platform, item);
                        }}
                        selectedCrop={Editor.defaultCropRatio}
                        platform={Editor.platform}
                      />
                    </div>

                    <div
                      // style={{ minHeight: "500px" }}
                      className="w-100 h-100 flex-fill"
                      key={`${Editor.platform}-${componentKey}`}
                    >
                      <div className=" w-100 h-100 position-relative d-flex flex-column gap-4">
                        {!editorInitArray.find(
                          (editor) => editor.platform === Editor.platform
                        )?.isInitialized && (
                          <div
                            style={{ zIndex: 12, top: '0px', left: 'Opx' }}
                            className="position-absolute w-100 h-100 bg-opacity-20% rounded-3 d-flex justify-content-center align-items-center bg-white"
                          >
                            <div className="w-50 h-50 d-flex flex-column justify-content-center align-items-center gap-2">
                              <span
                                className="spinner-border spinner-border-sm text-muted w-30px h-30px fs-2"
                                role="status"
                                aria-hidden="true"
                                style={{ aspectRatio: 1 }}
                              ></span>

                              <div>{t('lomavisCreator.multiEdit.Loading')}</div>
                            </div>
                          </div>
                        )}
                        {!editorChanged && (
                          <div
                            onClick={() => {
                              dispatch(
                                updateMultiImageEditorCloseState({
                                  state: true
                                })
                              );
                              setEditorChanged(true);
                            }}
                            style={{ zIndex: 11, top: '0px', left: 'Opx' }}
                            className="position-absolute w-100 h-100 bg-opacity-20% rounded-3"
                          ></div>
                        )}{' '}
                        <div
                          style={{
                            height: '66.67vh',
                            overflowY: 'hidden'
                          }}
                          className="w-100 bg-white p-3 rounded-3 d-flex flex-column"
                        >
                          <div className="h-100 w-100">
                            <div
                              style={{
                                height: synchronousEditing
                                  ? `${topDivHeight}px`
                                  : '0'
                              }}
                              className="w-100"
                            ></div>
                            <div
                              style={{
                                height: synchronousEditing
                                  ? `calc(484px - ${topDivHeight}px)`
                                  : '100%'
                              }}
                              className="w-100 d-flex justify-content-between"
                            >
                              <div
                                style={{
                                  width: synchronousEditing
                                    ? `${displaceWidth}px`
                                    : '0'
                                }}
                                className="h-100"
                              ></div>
                              <div
                                style={{
                                  width:
                                    synchronousEditing && divWidth
                                      ? `${divWidth}px`
                                      : '100%',
                                  height: '66.67vh'
                                }}
                                className={`bg-white ${
                                  !synchronousEditing && 'py-3'
                                }`}
                              >
                                <PinturaEditor
                                  {...editorDefaults}
                                  ref={Editor.editorRef}
                                  stickers={[
                                    ['Emoji', [...EditorImageConfig?.Stickers]],
                                    ['Markers', []]
                                  ]}
                                  className="w-100"
                                  src={
                                    Editor.imageInfo[currentIndex].assetBlob
                                      ? Editor.imageInfo[currentIndex].assetBlob
                                      : Editor.imageInfo[currentIndex]
                                          .original_asset
                                  }
                                  cropInteractionFocus={'selection'}
                                  willRenderToolbar={willRenderToolbar}
                                  stickerStickToImage={true}
                                  onUpdate={(imageState) =>
                                    handleEditor2Update(imageState, index)
                                  }
                                  onReady={() => {
                                    handleEditorReady(Editor.platform);
                                  }}
                                  onInit={() =>
                                    handleEditorInit(Editor.platform)
                                  }
                                  enableZoom={false}
                                  enableZoomControls={false}
                                  enableUtils={
                                    synchronousEditing ? false : true
                                  }
                                  enableToolbar={
                                    synchronousEditing ? false : true
                                  }
                                  enableNavigateHistory={
                                    synchronousEditing ? false : true
                                  }
                                  onProcess={handleEditorProcess}
                                ></PinturaEditor>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {EditorArrayInfo?.slice(editorsToLoad, EditorArrayInfo?.length).map(
              (Editor, index) => {
                return (
                  <div className="d-flex gap-4 flex-column loading">
                    <div
                      style={{}}
                      className="h-100 w-100 h-60px d-flex justify-content-between headerDiv"
                    >
                      <ContentLoader
                        speed={1}
                        width={headerDimensions.width}
                        height={headerDimensions.height}
                        backgroundColor="#f6f5f4"
                        foregroundColor="#deddda"
                        className="w-100 h-100"
                      >
                        <rect
                          x="49"
                          y="202"
                          rx="0"
                          ry="0"
                          width="0"
                          height="12"
                        />
                        <rect
                          x="0"
                          y="0"
                          rx="10"
                          ry="10"
                          width={headerDimensions.width}
                          height={headerDimensions.height}
                        />
                      </ContentLoader>
                    </div>
                    <div className="d-flex w-100 h-100 gap-4">
                      <div
                        style={{
                          overflowY: 'auto',
                          width: '100px',
                          height: '500px'
                        }}
                        className="asideDiv"
                      >
                        <ContentLoader
                          speed={1}
                          width={asideDimensions.width}
                          height={asideDimensions.height}
                          backgroundColor="#f6f5f4"
                          foregroundColor="#deddda"
                          className="w-100 h-100"
                        >
                          <rect
                            x="49"
                            y="202"
                            rx="0"
                            ry="0"
                            width="0"
                            height="12"
                          />
                          <rect
                            x="0"
                            y="0"
                            rx="10"
                            ry="10"
                            width={asideDimensions.width}
                            height={asideDimensions.height}
                          />
                        </ContentLoader>
                      </div>

                      <div
                        // style={{ minHeight: "500px" }}
                        className="w-100 h-100 flex-fill"
                        key={`${Editor.platform}-${componentKey}`}
                      >
                        <div className=" w-100 h-100 position-relative d-flex flex-column gap-4">
                          <div
                            style={{
                              height: '500px'
                            }}
                            className="w-100 d-flex flex-column editorDiv"
                          >
                            <ContentLoader
                              speed={1}
                              width={editorDivDimensions.width}
                              height={editorDivDimensions.height}
                              backgroundColor="#f6f5f4"
                              foregroundColor="#deddda"
                              className="w-100 h-100"
                            >
                              <rect
                                x="49"
                                y="202"
                                rx="0"
                                ry="0"
                                width="0"
                                height="12"
                              />
                              <rect
                                x="0"
                                y="0"
                                rx="10"
                                ry="10"
                                width={editorDivDimensions.width}
                                height={editorDivDimensions.height}
                              />
                            </ContentLoader>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        )}

        {currentView === UPLOAD_VIEW && (
          <div className="w-100 bg-white p-3 h-100 rounded-3">
            <div
              style={{
                width: '80%',
                height: '90%'
              }}
              className="mx-auto align-items-center justify-content-center d-flex flex-column gap-4"
            >
              <div className="w-100 h-100 position-relative">
                <img
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%'
                  }}
                  className="position-absolute top-50 start-50 translate-middle object-fit-contain"
                  src={currentImage}
                  alt="Image preview"
                />
              </div>
              <div
                style={{ height: '14px' }}
                className="w-50 d-flex flex-column justify-content-center"
              >
                <ProgressBar
                  className="h-100"
                  variant="primary"
                  animated
                  now={getTotalProgressPercentage()}
                />
              </div>{' '}
              {/* <h4>
                {uploadCount}/{EditorArrayInfo.length}
              </h4> */}
            </div>
          </div>
        )}

        <div
          ref={footerRef}
          style={{ bottom: '0', zIndex: 100 }}
          className="w-100 d-flex justify-content-end gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px bg-white"
        >
          <div
            style={{ marginRight: rightMargin + 'px' }}
            className="d-flex justify-content-end gap-3 align-items-center py-2 flax-fill"
          >
            {!startSubmission && !processingImage && (
              <Button
                onClick={() => {
                  currentView === UPLOAD_VIEW
                    ? setCurrentView(EDITOR_VIEW)
                    : handleClose();
                }}
                variant="secondary"
                size="sm"
                disabled={startSubmission || processingImage}
              >
                {t('lomavisCreator.back')}
              </Button>
            )}

            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                exportImage();
              }}
              disabled={
                startSubmission ||
                processingImage ||
                !editorInitArray.every((editor) => editor.isInitialized)
              }
            >
              {(startSubmission || processingImage) && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              {startSubmission || processingImage
                ? t('lomavisCreator.buttons.saving')
                : t('lomavisCreator.buttons.save')}
            </Button>
          </div>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleArrayInputChange}
        max={1}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default Editors;
